import React, { createContext, useState, useContext, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  year7aStudents, year7bStudents, year8Students, year9Students, year10Students,
  year11aStudents, year11cStudents, year11sStudents,
  year12aStudents, year12cStudents, year12sStudents,
  year13aStudents, year13ComputerStudiesStudents, year13sStudents
} from '../data/realStudents';

const SyncContext = createContext();

export function useSync() {
  return useContext(SyncContext);
}

export function SyncProvider({ children }) {
  const [isOffline, setIsOffline] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Centralized Student Data Store (simulating database)
  const [studentDb, setStudentDb] = useState({});
  
  // Assessment Marks Store - maps studentId-assessmentId to marks
  // Structure: { "studentId-assessmentId": { rawMarks: 85, maxMarks: 100, autoMarked: true, timestamp: ... } }
  const [assessmentMarks, setAssessmentMarks] = useState(() => {
    const saved = localStorage.getItem('assessmentMarks');
    return saved ? JSON.parse(saved) : {};
  });

  // Initialize DB from real student data on mount
  useEffect(() => {
    const initialDb = {};
    
    const initList = (names, grade) => {
      names.forEach((name, i) => {
        const id = `${grade.replace(/\s+/g,'')}-${i}`;
        // Simulate random attendance history
        const totalDays = 50;
        const absentCount = Math.floor(Math.random() * 6); // 0-5 absences
        const presentCount = totalDays - absentCount;
        const percentage = Math.round((presentCount / totalDays) * 100);

        // Create mock history
        const history = [];
        const today = new Date();
        for (let d = 0; d < totalDays; d++) {
          const date = new Date(today);
          date.setDate(date.getDate() - d);
          // Simple mock history generation
          const isAbsent = d < absentCount;
          history.push({
            date: date.toISOString().split('T')[0],
            status: isAbsent ? 'absent' : 'present'
          });
        }

        initialDb[id] = {
          id,
          name,
          grade,
          email: `${name.toLowerCase().replace(/\s+/g, '.')}@penama.edu.vu`,
          attendance: {
            present: presentCount,
            absent: absentCount,
            late: 0,
            total: totalDays,
            percentage: percentage,
            history: history
          },
          referralStatus: absentCount >= 4 ? 'pending_referral' : 'none',
          archivedTerms: [], // Store past term data here
          droppedSubjects: [] // Track dropped subjects with date and reason
        };
      });
    };

    // Initialize all classes with real student data from St Patrick's College
    // All 343 students across 13 classes ready for curriculum uploads
    initList(year7aStudents, "Year 7A");
    initList(year7bStudents, "Year 7B");
    initList(year8Students, "Year 8");
    initList(year9Students, "Year 9");
    initList(year10Students, "Year 10");
    initList(year11aStudents, "Year 11A");
    initList(year11cStudents, "Year 11C");
    initList(year11sStudents, "Year 11S");
    initList(year12aStudents, "Year 12A");
    initList(year12cStudents, "Year 12C");
    initList(year12sStudents, "Year 12S");
    initList(year13aStudents, "Year 13A");
    initList(year13ComputerStudiesStudents, "Year 13 Computer Studies");
    initList(year13sStudents, "Year 13S");

    setStudentDb(initialDb);
  }, []);

  const toggleOfflineMode = () => {
    setIsOffline(prev => {
      const newState = !prev;
      toast.info(newState ? "Offline Mode Enabled" : "Online Mode Enabled");
      return newState;
    });
  };

  const addChange = (type, data) => {
    const newChange = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      type, 
      data,
      status: 'pending'
    };
    
    setPendingChanges(prev => [...prev, newChange]);
    
    // Optimistic Updates
    if (type === 'roll_call_submit') {
      handleRollCallUpdate(data);
    } else if (type === 'period_roll_call') {
      handlePeriodRollCallUpdate(data);
    } else if (type === 'referral_created') {
      handleReferralUpdate(data.studentId);
    } else if (type === 'reset_absences') {
      handleResetAbsences(data.studentId);
    } else if (type === 'reset_attendance_scope') {
      handleResetAttendanceScope(data);
    } else if (type === 'reset_term_attendance') {
      handleTermReset(data);
    }

    if (isOffline) {
      toast.success("Saved offline. Will sync when online.");
    } else {
      toast.success("Data recorded. Ready to sync.");
    }
  };

  const handleRollCallUpdate = (data) => {
    setStudentDb(prev => {
      const newDb = { ...prev };
      data.attendance.forEach(record => {
        const student = newDb[record.id];
        if (student) {
          // Add new record to history
          const existingIndex = student.attendance.history.findIndex(h => h.date === data.date);
          if (existingIndex >= 0) {
            student.attendance.history[existingIndex].status = record.status;
          } else {
            student.attendance.history.push({ date: data.date, status: record.status });
          }
          
          // Recalculate stats from history
          const stats = recalculateStats(student.attendance.history);
          student.attendance = { ...student.attendance, ...stats };
          
          // Update referral status
          if (student.attendance.absent >= 4 && student.referralStatus === 'none') {
            student.referralStatus = 'pending_referral';
          }
        }
      });
      return newDb;
    });
  };

  const handlePeriodRollCallUpdate = (data) => {
    setStudentDb(prev => {
      const newDb = { ...prev };
      data.attendance.forEach(record => {
        const student = newDb[record.studentId];
        if (student) {
          // For period-based attendance, we track by date and period
          const periodKey = `${data.date}_${data.period}`;
          
          if (!student.periodAttendance) {
            student.periodAttendance = {};
          }
          
          student.periodAttendance[periodKey] = {
            date: data.date,
            period: data.period,
            status: record.status,
            class: data.class
          };
          
          // Also update the daily history if absent for any period
          if (record.status === 'absent' || record.status === 'late') {
            const existingIndex = student.attendance.history.findIndex(h => h.date === data.date);
            if (existingIndex >= 0) {
              // Keep the worst status (absent > late > present)
              const current = student.attendance.history[existingIndex].status;
              if (current === 'present' || (current === 'late' && record.status === 'absent')) {
                student.attendance.history[existingIndex].status = record.status;
              }
            } else {
              student.attendance.history.push({ date: data.date, status: record.status });
            }
            
            // Recalculate stats
            const stats = recalculateStats(student.attendance.history);
            student.attendance = { ...student.attendance, ...stats };
            
            // Update referral status
            if (student.attendance.absent >= 4 && student.referralStatus === 'none') {
              student.referralStatus = 'pending_referral';
            }
          }
        }
      });
      return newDb;
    });
  };

  const recalculateStats = (history) => {
    let present = 0, absent = 0, late = 0;
    history.forEach(h => {
      if (h.status === 'present') present++;
      if (h.status === 'absent') absent++;
      if (h.status === 'late') late++;
    });
    const total = history.length;
    const percentage = total === 0 ? 100 : Math.round((present / total) * 100);
    return { present, absent, late, total, percentage, history };
  };

  const handleReferralUpdate = (studentId) => {
    setStudentDb(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        referralStatus: 'referred'
      }
    }));
  };

  const handleResetAbsences = (studentId) => {
    setStudentDb(prev => {
      const student = prev[studentId];
      const newStats = {
        present: 0,
        absent: 0,
        late: 0,
        total: 0,
        percentage: 100,
        history: []
      };

      return {
        ...prev,
        [studentId]: {
          ...student,
          attendance: newStats,
          referralStatus: 'none'
        }
      };
    });
  };

  const handleResetAttendanceScope = ({ className, scope, referenceDate }) => {
    setStudentDb(prev => {
      const newDb = { ...prev };
      const year = className.includes("Year 10") ? "Year 10" :
                   className.includes("Year 12") ? "Year 12" :
                   className.includes("Year 13") ? "Year 13" : "";
      
      const refDate = new Date(referenceDate);

      Object.keys(newDb).forEach(key => {
        if (newDb[key].grade === year) {
          const student = newDb[key];
          
          const filteredHistory = student.attendance.history.filter(record => {
            const recordDate = new Date(record.date);
            
            if (scope === 'Today') {
              return record.date !== referenceDate; 
            } else if (scope === 'This Week') {
              const day = refDate.getDay(); 
              const diff = refDate.getDate() - day + (day === 0 ? -6 : 1); 
              const monday = new Date(refDate);
              monday.setDate(diff);
              monday.setHours(0,0,0,0);
              
              const sunday = new Date(monday);
              sunday.setDate(monday.getDate() + 6);
              sunday.setHours(23,59,59,999);
              
              return recordDate < monday || recordDate > sunday;
            } else if (scope === 'This Month') {
              return recordDate.getMonth() !== refDate.getMonth() || recordDate.getFullYear() !== refDate.getFullYear();
            }
            return true;
          });

          const newStats = recalculateStats(filteredHistory);
          newDb[key].attendance = newStats;
          
          if (newStats.absent < 4 && newDb[key].referralStatus !== 'referred') {
             newDb[key].referralStatus = 'none';
          }
        }
      });
      return newDb;
    });
  };

  // UPDATED: Proper Term Reset Implementation
  const handleTermReset = ({ termName }) => {
    setStudentDb(prev => {
      const newDb = { ...prev };
      Object.keys(newDb).forEach(key => {
        const student = newDb[key];
        
        // 1. Archive current stats
        const archiveRecord = {
          term: termName,
          dateArchived: new Date().toISOString(),
          finalStats: { 
            present: student.attendance.present,
            absent: student.attendance.absent,
            late: student.attendance.late,
            total: student.attendance.total,
            percentage: student.attendance.percentage
          }
        };
        
        if (!student.archivedTerms) student.archivedTerms = [];
        student.archivedTerms.push(archiveRecord);

        // 2. Reset active counters for new term
        student.attendance = {
          present: 0,
          absent: 0,
          late: 0,
          total: 0,
          percentage: 0, // Start at 0% for new term as requested
          history: [] // Clear daily history
        };
        
        // 3. Clear Risk Flags
        student.referralStatus = 'none';
      });
      return newDb;
    });
  };

  const getStudent = (id) => studentDb[id];
  
  const getStudentsByClass = (className) => {
    // Year 13 - Computer Studies (specific match first)
    if (className.includes("Year 13") && className.includes("Computer")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 13 Computer Studies");
    }
    // Year 13S
    if (className.includes("Year 13S") || className.includes("Year 13 S")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 13S");
    }
    // Year 13A
    if (className.includes("Year 13A") || className.includes("Year 13 A")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 13A");
    }
    
    // Year 12
    if (className.includes("Year 12S") || className.includes("Year 12 S")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 12S");
    }
    if (className.includes("Year 12C") || className.includes("Year 12 C")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 12C");
    }
    if (className.includes("Year 12A") || className.includes("Year 12 A")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 12A");
    }
    
    // Year 11
    if (className.includes("Year 11S") || className.includes("Year 11 S")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 11S");
    }
    if (className.includes("Year 11C") || className.includes("Year 11 C")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 11C");
    }
    if (className.includes("Year 11A") || className.includes("Year 11 A")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 11A");
    }
    
    // Year 10
    if (className.includes("Year 10")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 10");
    }
    
    // Year 9
    if (className.includes("Year 9")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 9");
    }
    
    // Year 8
    if (className.includes("Year 8")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 8");
    }
    
    // Year 7
    if (className.includes("Year 7B") || className.includes("Year 7 B")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 7B");
    }
    if (className.includes("Year 7A") || className.includes("Year 7 A")) {
      return Object.values(studentDb).filter(s => s.grade === "Year 7A");
    }
    
    // Return empty for classes without real student data
    return [];
  };

  const syncNow = async () => {
    if (pendingChanges.length === 0) {
      toast.info("No changes to sync.");
      return;
    }
    if (isOffline) {
      toast.error("Cannot sync while in Offline Mode.");
      return;
    }
    setIsSyncing(true);
    setTimeout(() => {
      setPendingChanges([]);
      setIsSyncing(false);
      toast.success("Sync Complete! All data uploaded to server.");
    }, 2000);
  };

  // Drop subject function
  const dropSubject = (studentId, subjectName, reason = '') => {
    setStudentDb(prev => {
      const student = prev[studentId];
      if (!student) return prev;

      const droppedSubject = {
        subjectName,
        dropDate: new Date().toISOString(),
        reason,
        gradesAtDrop: {} // Can store grades earned before dropping
      };

      return {
        ...prev,
        [studentId]: {
          ...student,
          droppedSubjects: [...(student.droppedSubjects || []), droppedSubject]
        }
      };
    });

    addChange('drop_subject', {
      studentId,
      subjectName,
      reason,
      timestamp: new Date().toISOString()
    });

    toast.success(`${subjectName} dropped for student`);
  };

  // Check if student has dropped a subject
  const hasDroppedSubject = (studentId, subjectName) => {
    const student = studentDb[studentId];
    if (!student || !student.droppedSubjects) return false;
    return student.droppedSubjects.some(ds => ds.subjectName === subjectName);
  };

  // Get dropped subject details
  const getDroppedSubjectInfo = (studentId, subjectName) => {
    const student = studentDb[studentId];
    if (!student || !student.droppedSubjects) return null;
    return student.droppedSubjects.find(ds => ds.subjectName === subjectName);
  };

  // Re-enroll in subject (undo drop)
  const reEnrollSubject = (studentId, subjectName) => {
    setStudentDb(prev => {
      const student = prev[studentId];
      if (!student) return prev;

      return {
        ...prev,
        [studentId]: {
          ...student,
          droppedSubjects: (student.droppedSubjects || []).filter(
            ds => ds.subjectName !== subjectName
          )
        }
      };
    });

    addChange('reenroll_subject', {
      studentId,
      subjectName,
      timestamp: new Date().toISOString()
    });

    toast.success(`Student re-enrolled in ${subjectName}`);
  };

  // Save assessment marks from auto-grading
  const saveAssessmentMark = (studentId, assessmentId, rawMarks, maxMarks, autoMarked = true) => {
    const key = `${studentId}-${assessmentId}`;
    const markData = {
      rawMarks,
      maxMarks,
      autoMarked,
      timestamp: new Date().toISOString()
    };
    
    setAssessmentMarks(prev => {
      const updated = { ...prev, [key]: markData };
      localStorage.setItem('assessmentMarks', JSON.stringify(updated));
      return updated;
    });
  };

  // Get assessment mark for a student
  const getAssessmentMark = (studentId, assessmentId) => {
    const key = `${studentId}-${assessmentId}`;
    return assessmentMarks[key] || null;
  };

  // Get all marks for a student
  const getStudentMarks = (studentId) => {
    const marks = {};
    const prefix = studentId + '-';
    Object.keys(assessmentMarks).forEach(key => {
      if (key.startsWith(prefix)) {
        const assessmentId = key.substring(prefix.length); // Get everything after studentId-
        marks[assessmentId] = assessmentMarks[key];
      }
    });
    return marks;
  };

  return (
    <SyncContext.Provider value={{
      isOffline,
      toggleOfflineMode,
      pendingChanges,
      addChange,
      syncNow,
      isSyncing,
      studentDb,
      getStudent,
      getStudentsByClass,
      dropSubject,
      hasDroppedSubject,
      getDroppedSubjectInfo,
      reEnrollSubject,
      saveAssessmentMark,
      getAssessmentMark,
      getStudentMarks,
      assessmentMarks
    }}>
      {children}
    </SyncContext.Provider>
  );
}
